import { type AnyRouteMatch, useMatches } from "@tanstack/react-router"
import { useMemo } from "react"

import type { BreadcrumbItem } from "../types"

type BreadcrumbResolver = (opts: {
  match: AnyRouteMatch
  matches: AnyRouteMatch[]
}) => string | false | null | undefined

type BreadcrumbStaticData = {
  breadcrumb?: string | false | BreadcrumbResolver | null
}

function toTitleCase(raw: string) {
  return raw
    .replace(/^\$/, "")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export function normalizeRoutePattern(path: string) {
  if (!path || path === "/") {
    return "/"
  }
  return path.endsWith("/") ? path.slice(0, -1) : path
}

function getStaticBreadcrumbLabel(match: AnyRouteMatch, matches: AnyRouteMatch[]) {
  const breadcrumb = (match.staticData as BreadcrumbStaticData | undefined)?.breadcrumb
  if (typeof breadcrumb === "function") {
    return breadcrumb({ match, matches })
  }
  return breadcrumb
}

export function useBreadcrumbs(config: {
  labelsByRoutePattern: Record<string, string>
  normalizeRoutePattern?: (path: string) => string
}): BreadcrumbItem[] {
  const matches = useMatches()
  const normalize = config.normalizeRoutePattern ?? normalizeRoutePattern

  return useMemo(() => {
    const crumbs: Array<{ key: string; label: string; href: string }> = []

    function getFallbackLabel(match: AnyRouteMatch) {
      const routePattern = normalize(match.fullPath)
      const mappedLabel = config.labelsByRoutePattern[routePattern]
      if (mappedLabel) {
        return mappedLabel
      }

      const fallbackSegment = routePattern.split("/").filter(Boolean).at(-1)
      if (!fallbackSegment) {
        return null
      }

      return toTitleCase(fallbackSegment)
    }

    for (const match of matches) {
      if (match.routeId === "__root__") {
        continue
      }

      const routePattern = normalize(match.fullPath)
      const staticLabel = getStaticBreadcrumbLabel(match, matches)

      if (staticLabel === false || staticLabel === null) {
        continue
      }

      const label = (staticLabel ?? getFallbackLabel(match))?.trim()
      if (!label) {
        continue
      }

      const href = normalize(match.pathname)
      const previous = crumbs[crumbs.length - 1]
      if (previous && previous.key === routePattern) {
        previous.label = label
        previous.href = href
        continue
      }

      crumbs.push({
        key: routePattern,
        label,
        href,
      })
    }

    return crumbs.map((crumb, index) => ({
      key: crumb.key,
      label: crumb.label,
      href: index === crumbs.length - 1 ? undefined : crumb.href,
      isCurrent: index === crumbs.length - 1,
    })) satisfies BreadcrumbItem[]
  }, [matches, normalize, config.labelsByRoutePattern])
}
